import React, { useEffect } from "react";
import FeedMediaComposer from "../components/FeedMediaComposer";
import MediaGrid from "../components/MediaGrid";

export default function useMobileComposer({
  showComposer,
  setShowComposer,
  avatarUrl,
  text,
  setText,
  mediaFiles,
  mediaPreview,
  uploadProgress,
  isPosting,
  handleMediaSelect,
  handlePost,
  quotePost,
  setQuotePost,
  setMediaFiles,
  setMediaPreview,
  setIsReplyMode,
  setReplyToPost,
}) {
  // Hide the global bottom tab bar while the mobile composer is open.
  // Must stay above the early return so hook order never changes.
  useEffect(() => {
    if (!showComposer || window.innerWidth >= 768) return;
    const root = document.documentElement;
    root.classList.add("keyboard-compose");
    return () => root.classList.remove("keyboard-compose");
  }, [showComposer]);

  // Only show on mobile (< 768px); desktop uses useDesktopComposer
  if (!showComposer || window.innerWidth >= 768) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-white dark:bg-neutral-900 flex flex-col"
      style={{
        /* Use dvh so the sheet shrinks when the soft keyboard rises */
        height: "100dvh",
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      {/* ── TOP BAR ── */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
        <button
          onClick={() => {
            setShowComposer(false);
            setText("");
            setMediaFiles([]);
            setMediaPreview([]);
            setIsReplyMode(false);
            setReplyToPost(null);
            setQuotePost(null);
          }}
          className="text-blue-500 font-medium text-sm touch-manipulation active:opacity-60"
          style={{ minHeight: 44, paddingRight: 8 }}
        >
          Cancel
        </button>

        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
          New Post
        </span>

        <button
          onClick={handlePost}
          disabled={isPosting || (!text.trim() && mediaFiles.length === 0 && !quotePost)}
          className={[
            "px-4 py-1.5 rounded-full font-semibold text-sm transition-all touch-manipulation active:scale-95",
            isPosting || (!text.trim() && mediaFiles.length === 0 && !quotePost)
              ? "bg-blue-300 text-white opacity-60"
              : "bg-blue-500 hover:bg-blue-600 text-white",
          ].join(" ")}
          style={{ minHeight: 36 }}
        >
          {isPosting ? "Posting…" : "Post"}
        </button>
      </div>

      {/* ── SCROLLABLE BODY ── */}
      <div
        className="flex-1 overflow-y-auto overscroll-contain"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {/* AVATAR + TEXTAREA */}
        <div className="flex gap-3 px-4 py-3">
          <div className="w-10 h-10 shrink-0">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Me"
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-neutral-300 dark:bg-neutral-700 flex items-center justify-center text-base">
                👤
              </div>
            )}
          </div>
          <textarea
  id="feed-post-textarea-mobile"
  name="feed-post-mobile"
  autoComplete="off"
  autoFocus
  value={text}
  onChange={(e) => setText(e.target.value)}
  placeholder="Share your ideas, experiences, opinions, information, and stories."
  rows={5}
  className="flex-1 resize-none bg-transparent outline-none text-[17px] leading-relaxed text-neutral-900 dark:text-neutral-100 placeholder-neutral-400"
/>
        </div>

        {/* MEDIA PREVIEWS */}
        <div className="px-4">
          <FeedMediaComposer
            mediaFiles={mediaFiles}
            mediaPreview={mediaPreview}
            uploadProgress={uploadProgress}
            isPosting={isPosting}
            onSelectMedia={handleMediaSelect}
            showProgress
          />
        </div>

        {/* QUOTE CARD */}
        {quotePost && (
          <div className="px-4 pt-2 pb-1">
            <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
              <div className="px-3 py-2 bg-neutral-50 dark:bg-neutral-800">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-xs text-neutral-500 mb-1">
                    🔁 Quoting @{quotePost.author?.username}
                  </div>
                  <button
                    onClick={() => setQuotePost(null)}
                    className="text-neutral-400 text-lg leading-none touch-manipulation -mt-0.5"
                    aria-label="Remove quote"
                  >
                    ×
                  </button>
                </div>
                {quotePost.text && (
                  <div className="text-sm whitespace-pre-wrap text-neutral-700 dark:text-neutral-300">
                    {quotePost.text}
                  </div>
                )}
                {quotePost.media?.length > 0 && (
                  <div className="mt-2">
                    <MediaGrid media={quotePost.media} />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}